/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 * the European Commission - subsequent versions of the EUPL (the "Licence");
 * You may not use this work except in compliance with the Licence.
 * You may obtain a copy of the Licence at:
 *
 *   https://joinup.ec.europa.eu/software/page/eupl
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the Licence is distributed on an "AS IS" basis,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the Licence for the specific language governing permissions and
 * limitations under the Licence. */

import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import React from "react";
import { useIntl } from "react-intl";
import { Entities } from "../../../../models/Entities";
import ModalityIconImg from "../../../MainPage/ModalityIconImg";
import { CenterMapButton, CopyIdButton, FavoriteButton } from "../../Shared";
import {
  hasChangedKey,
  useElementStatusEnabled,
  UnsavedDot,
  useStopPlaceDirtyKeys,
} from "../../Shared/ElementStatus";
import { StopPlace } from "../types";
import { PanelToggleIcon } from "../../Shared";
import { StopStatusBanner } from "../../Shared/StopStatus";

/** The header owns no fields of its own; it mirrors the stop place name. */
const HEADER_NAME_KEYS = ["name"] as const;

const MODALITY_ICON_SIZE = 32;

const NAME_FONT_SIZE_SHORT = "1.5rem";
const NAME_FONT_SIZE_MEDIUM = "1.3rem";
const NAME_FONT_SIZE_LONG = "1.1rem";
const NAME_FONT_SIZE_XLONG = "1rem";
const NAME_LENGTH_THRESHOLD_SHORT = 20;
const NAME_LENGTH_THRESHOLD_MEDIUM = 26;
const NAME_LENGTH_THRESHOLD_LONG = 32;

const resolveFontSize = (nameLength: number): string => {
  if (nameLength <= NAME_LENGTH_THRESHOLD_SHORT) return NAME_FONT_SIZE_SHORT;
  if (nameLength <= NAME_LENGTH_THRESHOLD_MEDIUM) return NAME_FONT_SIZE_MEDIUM;
  if (nameLength <= NAME_LENGTH_THRESHOLD_LONG) return NAME_FONT_SIZE_LONG;
  return NAME_FONT_SIZE_XLONG;
};

export interface StopPlaceHeaderProps {
  stopPlace: StopPlace;
  stopName: string;
  /** Called when the close (X) or back (←) button is clicked. Not rendered when undefined. */
  onClose?: () => void;
  /** Called to toggle the drawer open/closed. */
  onToggle: () => void;
  /** True = drawer is open → show ExpandLess. False = drawer is collapsed → show ExpandMore. */
  isExpanded: boolean;
}

/**
 * Shared stop place header row used in both the full expanded drawer and the
 * collapsed MinimizedBar. Renders:
 *
 *   [X/←?] [stop type icon] [name (scaled) + id caption] [center] [★ fav] [expand/collapse]
 *   [⚠ expired banner — conditional]
 */
export const StopPlaceHeader: React.FC<StopPlaceHeaderProps> = ({
  stopPlace,
  stopName,
  onClose,
  onToggle,
  isExpanded,
}) => {
  const { formatMessage } = useIntl();
  const isStatusEnabled = useElementStatusEnabled();
  const dirtyKeys = useStopPlaceDirtyKeys();
  const isNameDirty =
    isStatusEnabled && hasChangedKey(dirtyKeys, HEADER_NAME_KEYS);
  const nameLength = stopName?.length ?? 0;
  const nameFontSize = resolveFontSize(nameLength);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          minHeight: 48,
          gap: 0.5,
        }}
      >
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          <ModalityIconImg
            type={stopPlace.stopPlaceType || "other"}
            submode={stopPlace.submode}
            svgStyle={{ width: MODALITY_ICON_SIZE, height: MODALITY_ICON_SIZE }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* The dot is a sibling, not a child: an inline-flex wrapper inside the
              Typography would defeat the noWrap ellipsis on long names. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: nameFontSize,
                lineHeight: 1.2,
                minWidth: 0,
              }}
              noWrap={nameLength > NAME_LENGTH_THRESHOLD_MEDIUM}
            >
              {stopName}
            </Typography>
            {isNameDirty && <UnsavedDot />}
          </Box>
          {stopPlace.topographicPlace && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block" }}
            >
              {stopPlace.parentTopographicPlace
                ? `${stopPlace.topographicPlace}, ${stopPlace.parentTopographicPlace}`
                : stopPlace.topographicPlace}
            </Typography>
          )}
          {stopPlace.id && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                mt: -0.25,
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {stopPlace.id}
              </Typography>
              <CopyIdButton idToCopy={stopPlace.id} size="small" />
            </Box>
          )}
        </Box>

        {/* Actions on the stop place itself: where it is, and saving it as a favourite. */}
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <CenterMapButton location={stopPlace.location} />

          {stopPlace.id && (
            <FavoriteButton
              id={stopPlace.id}
              name={stopPlace.name}
              entityType={Entities.STOP_PLACE}
              stopPlaceType={stopPlace.stopPlaceType}
              submode={stopPlace.submode ?? undefined}
              topographicPlace={stopPlace.topographicPlace}
              parentTopographicPlace={stopPlace.parentTopographicPlace}
              location={stopPlace.location}
            />
          )}
        </Box>

        {/* Separates the two kinds of action: the group above acts on the stop
            place, the group below acts on this panel. They were one flat row. */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

        {/* Window controls: what happens to the panel, not to the stop place. */}
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <Tooltip
            title={formatMessage({ id: isExpanded ? "collapse" : "expand" })}
          >
            <IconButton size="small" onClick={onToggle}>
              <PanelToggleIcon isExpanded={isExpanded} />
            </IconButton>
          </Tooltip>

          {onClose && (
            <Tooltip title={formatMessage({ id: "close" })}>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <StopStatusBanner stopPlace={stopPlace} />
    </>
  );
};
